import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { CaretDown, CheckCircle, HourglassMedium } from '@phosphor-icons/react';
import classNames from 'classnames';
import { isMobile, isTablet } from 'react-device-detect';

interface Props {
  children: React.ReactNode;
  heading: string;
  subheading: string | React.ReactNode;
  headingBtn?: React.ReactNode;
  progressText?: string;
  isOpen: boolean;
  isDisabled?: boolean;
}

const VBAccordion: React.FC<Props> = (props) => {
  const {
    children,
    heading,
    subheading,
    headingBtn,
    progressText,
    isOpen,
    isDisabled = false,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(isOpen && !isDisabled);
  }, [isDisabled, isOpen]);

  const isCompleted = progressText === 'Completed';

  return (
    <Accordion
      className="w-full border border-grey-200 border-solid rounded-md"
      expanded={isExpanded}
      onChange={() => setIsExpanded(!isExpanded)}
      disableGutters
      sx={{ ':before': { display: 'none' } }}
      disabled={isDisabled}
    >
      <AccordionSummary
        expandIcon={<CaretDown weight="bold" size="1.2rem" />}
        className="items-start sm:items-center p-4 gap-1 sm:gap-4 cursor-pointer"
        sx={{ '.MuiAccordionSummary-content': { margin: '0' } }}
      >
        <div className="w-full flex items-start sm:items-center justify-between gap-2 sm:gap-4">
          <div className="flex flex-col gap-2">
            {heading && (
              <div className="flex items-center gap-2 text-xl font-bold uppercase leading-none">
                {heading}
                {headingBtn}
              </div>
            )}

            {subheading && <div>{subheading}</div>}
          </div>

          {!!progressText && !isDisabled && (
            <div
              className={classNames(
                'flex items-center gap-1 font-semibold whitespace-nowrap',
                { 'text-success-900': isCompleted },
              )}
            >
              {isCompleted ? (
                <CheckCircle weight="fill" size="1.2rem" />
              ) : (
                <HourglassMedium weight="fill" size="1.2rem" />
              )}

              {!isMobile && !isTablet && <div>{progressText}</div>}
            </div>
          )}
        </div>
      </AccordionSummary>

      {children && !isDisabled && (
        <AccordionDetails className="flex flex-col pt-0 gap-4">
          {children}
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default VBAccordion;
